/**
 * The player's own control bar on web.
 *
 * ## Why it replaces the browser's rather than sitting beside it
 *
 * It started as a fix for one engine. A remuxed stream is a live-generated fragmented MP4
 * — a pipe with no byte index — so the element reports a duration of `Infinity` and its
 * native scrubber is dead: it cannot show where you are, and dragging it does nothing.
 * Leaving the native controls on *and* adding a seek bar underneath produced two control
 * surfaces, one of which lied.
 *
 * It now runs for every engine on web, and the reason is the track menu. Chromium's bar
 * has no audio-track control at all, so on the engine where switching tracks works best —
 * hls.js — the feature had nowhere to live. Two bars, one per engine, would also have
 * meant the television and the laptop were visibly different products for no reason a
 * viewer could name.
 *
 * ## The two seek models
 *
 * Most engines seek by assigning `currentTime`. The remux engine cannot: the response is a
 * pipe, so seeking restarts the conversion at a new offset upstream and position becomes
 * `offset + video.currentTime`, where `offset` is the second ffmpeg was told to start at.
 * `seekRestarts` says which of the two is in play, and the only thing it changes here is
 * whether a seek is worth telling the viewer to wait for.
 *
 * Total length comes from the element when it knows, and from the provider's metadata when
 * it does not. Without either there is nothing truthful to draw, so the bar renders as a
 * plain elapsed counter rather than a scrubber that invents an end.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import { TrackMenu } from './TrackMenu';
import type { EngineTracks } from './engines';

/** How long the pointer may rest motionless over the picture before the bar withdraws. */
const IDLE_MS = 3000;

function clock(total: number): string {
  if (!Number.isFinite(total) || total < 0) return '0:00';
  const s = Math.floor(total);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
}

/**
 * Whether a key press belongs to whatever the viewer is currently interacting with.
 *
 * The bar's own sliders are range inputs, and arrow keys on a focused one must move the
 * slider rather than seek the film — otherwise the volume control cannot be operated by
 * keyboard at all. Space on a focused button must press the button, for the same reason.
 */
function belongsToTarget(event: KeyboardEvent): boolean {
  const el = event.target;
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return tag === 'BUTTON' && (event.key === ' ' || event.key === 'Enter');
}

export function PlayerControls({
  video,
  container,
  durationSec,
  offset,
  onSeek,
  seekRestarts = false,
  tracks = null,
  engineName,
  audioOffsetMs = 0,
  onAudioOffset,
}: {
  video: HTMLVideoElement | null;
  /** Element to make fullscreen — the wrapper, so these controls stay visible in it. */
  container: HTMLElement | null;
  /** The provider's figure, used only when the element cannot report its own. */
  durationSec: number | null;
  offset: number;
  onSeek: (seconds: number) => void;
  /** True when a seek restarts the stream upstream and therefore takes a few seconds. */
  seekRestarts?: boolean;
  /** Null where the engine cannot enumerate tracks; the menu explains that case itself. */
  tracks?: EngineTracks | null;
  engineName?: string | null;
  /** Positive delays the audio, negative advances it. */
  audioOffsetMs?: number;
  /** Omitted when the engine has no server-side audio to shift. */
  onAudioOffset?: (ms: number) => void;
}) {
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(offset);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  /**
   * The element's own idea of how long the film is.
   *
   * Preferred over the provider's metadata, which is frequently wrong or missing — the
   * file being played is the authority on its own length. `Infinity` is what a pipe
   * reports, and it is rejected here rather than drawn as an end that never arrives.
   */
  const [elementDuration, setElementDuration] = useState<number | null>(null);
  /** While dragging, the thumb follows the pointer instead of the still-playing video. */
  const [dragging, setDragging] = useState<number | null>(null);
  /** Set between a seek request and the first frame arriving, which takes a moment. */
  const [seeking, setSeeking] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  /** False once the pointer has rested still over the picture for {@link IDLE_MS}. */
  const [awake, setAwake] = useState(true);
  const seekingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!video) return;

    const onTime = () => setPosition(offset + video.currentTime);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onVolume = () => {
      setVolume(video.volume);
      setMuted(video.muted);
    };
    const onPlaying = () => {
      setSeeking(false);
      if (seekingTimer.current) clearTimeout(seekingTimer.current);
    };
    const onDuration = () => {
      const own = video.duration;
      setElementDuration(Number.isFinite(own) && own > 0 ? own : null);
    };

    video.addEventListener('timeupdate', onTime);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('volumechange', onVolume);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('durationchange', onDuration);
    video.addEventListener('loadedmetadata', onDuration);

    onTime();
    onVolume();
    onDuration();
    setPlaying(!video.paused);

    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('volumechange', onVolume);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('durationchange', onDuration);
      video.removeEventListener('loadedmetadata', onDuration);
    };
  }, [video, offset]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement != null);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const commitSeek = useCallback(
    (seconds: number) => {
      setPosition(seconds);
      onSeek(seconds);
      if (!seekRestarts) return;
      // The conversion restarts upstream, so `playing` may not fire for a few seconds.
      // Without a ceiling the spinner would stick forever if it never arrives.
      setSeeking(true);
      if (seekingTimer.current) clearTimeout(seekingTimer.current);
      seekingTimer.current = setTimeout(() => setSeeking(false), 15_000);
    },
    [onSeek, seekRestarts],
  );

  useEffect(
    () => () => {
      if (seekingTimer.current) clearTimeout(seekingTimer.current);
    },
    [],
  );

  // The element's figure wins; the provider's is the fallback. Read into a ref as well,
  // because the shortcut handler below must not be rebuilt whenever the duration settles.
  const total = elementDuration ?? durationSec;
  const seekable = total != null && total > 0;

  /*
   * The live position and length, for callbacks that must not be rebuilt to see them.
   *
   * `position` changes about four times a second. A `nudge` that depended on it would take
   * a new identity that often, and the keyboard effect below would tear down and re-bind
   * its window listener at the same rate — dropping any press that landed in the gap.
   */
  const live = useRef({ position, total });
  live.current = { position, total };

  const nudge = useCallback(
    (delta: number) => {
      const { position: now, total: length } = live.current;
      const max = length ?? Number.MAX_SAFE_INTEGER;
      const from = video ? offset + video.currentTime : now;
      commitSeek(Math.min(Math.max(0, from + delta), max));
    },
    [commitSeek, video, offset],
  );

  const toggleMute = useCallback(() => {
    if (!video) return;
    video.muted = !video.muted;
  }, [video]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void container?.requestFullscreen().catch(() => {});
  }, [container]);

  /*
   * Keyboard shortcuts.
   *
   * Bound to the window rather than to the stage, because the point of them is that they
   * work while the viewer's focus is anywhere on the page — a shortcut that first requires
   * clicking the video is a shortcut for nothing.
   *
   * `c` toggles subtitles against the first track the engine offers rather than opening
   * the menu. Turning captions on is the request behind that key nine times in ten, and
   * the menu is one press away for the tenth.
   */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (belongsToTarget(e)) return;

      switch (e.key) {
        case ' ':
          if (video) void (video.paused ? video.play().catch(() => {}) : video.pause());
          break;
        case 'ArrowLeft':
          nudge(-10);
          break;
        case 'ArrowRight':
          nudge(10);
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'm':
        case 'M':
          toggleMute();
          break;
        case 'c':
        case 'C': {
          const set = tracks?.get();
          const first = set?.subtitle[0];
          if (!set || !first) return; // nothing to toggle; leave the key alone
          tracks?.select('subtitle', set.activeSubtitle ? null : first.id);
          break;
        }
        case 'Escape':
          if (!menuOpen) return; // Escape also leaves fullscreen; do not steal it
          setMenuOpen(false);
          break;
        default:
          return;
      }
      e.preventDefault();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [video, nudge, toggleFullscreen, toggleMute, tracks, menuOpen]);

  /*
   * Withdraw the bar when the pointer stops moving.
   *
   * Hover alone keeps it up for as long as the pointer happens to rest over the picture,
   * which during a film is most of the time. The timer is re-armed on every move, and
   * cancelled entirely while the menu is open — a menu that vanished mid-read would be
   * worse than a bar that overstayed.
   */
  useEffect(() => {
    if (!container) return;
    if (menuOpen) {
      setAwake(true);
      return;
    }
    let timer = window.setTimeout(() => setAwake(false), IDLE_MS);
    const wake = () => {
      setAwake(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setAwake(false), IDLE_MS);
    };
    container.addEventListener('pointermove', wake);
    return () => {
      container.removeEventListener('pointermove', wake);
      window.clearTimeout(timer);
    };
  }, [container, menuOpen]);

  const shown = dragging ?? position;
  // Pinned open while paused, seeking, or with the menu up: a stopped picture with no
  // visible way to restart it is the worst state this bar can be in.
  const pinned = !playing || seeking || menuOpen;

  return (
    <>
      {menuOpen && (
        <TrackMenu tracks={tracks} engine={engineName} onClose={() => setMenuOpen(false)} />
      )}

      <div className={`pc${pinned || awake ? ' pc--shown' : ''}`}>
        <button
          type="button"
          className="pc__btn"
          onClick={() => (video?.paused ? void video.play() : video?.pause())}
          aria-label={playing ? 'Duraklat' : 'Oynat'}
        >
          <Icon name={playing ? 'pause' : 'play'} size={1} />
        </button>

        <button type="button" className="pc__btn" onClick={() => nudge(-10)} aria-label="10 saniye geri">
          <Icon name="rewind" size={1} />
        </button>
        <button type="button" className="pc__btn" onClick={() => nudge(30)} aria-label="30 saniye ileri">
          <Icon name="forward" size={1} />
        </button>

        <span className="pc__time">{clock(shown)}</span>

        {seekable ? (
          <input
            className="pc__seek"
            type="range"
            min={0}
            max={Math.floor(total)}
            value={Math.min(Math.floor(shown), Math.floor(total))}
            onChange={(e) => setDragging(Number(e.target.value))}
            // Committed on release, not on change: a seek on the remux engine restarts
            // ffmpeg and re-opens the upstream connection, so a dragged scrubber would
            // spawn one per pixel.
            onPointerUp={() => {
              if (dragging != null) commitSeek(dragging);
              setDragging(null);
            }}
            onKeyUp={(e) => {
              if (dragging != null && e.key.startsWith('Arrow')) {
                commitSeek(dragging);
                setDragging(null);
              }
            }}
            aria-label="Konum"
          />
        ) : (
          <span className="pc__seek pc__seek--none">süre bilgisi yok</span>
        )}

        <span className="pc__time">{seekable ? clock(total) : ''}</span>

        {seeking && <span className="pc__busy">konumlanıyor…</span>}

        <button
          type="button"
          className={`pc__btn${menuOpen ? ' is-active' : ''}`}
          onClick={() => setMenuOpen((open) => !open)}
          aria-label="Ses ve altyazı"
          aria-expanded={menuOpen}
        >
          <Icon name="subtitles" size={1} />
        </button>

        <button type="button" className="pc__btn" onClick={toggleMute} aria-label={muted ? 'Sesi aç' : 'Sesi kapat'}>
          <Icon name={muted || volume === 0 ? 'mute' : 'volume'} size={1} />
        </button>

        <input
          className="pc__volume"
          type="range"
          min={0}
          max={100}
          value={Math.round((muted ? 0 : volume) * 100)}
          onChange={(e) => {
            if (!video) return;
            const v = Number(e.target.value) / 100;
            video.volume = v;
            // Moving the slider off zero is an unmute; leaving it muted would make the
            // control appear broken.
            video.muted = v === 0;
          }}
          aria-label="Ses seviyesi"
        />

        {/*
          A/V sync trim, as VLC and mpv provide. Two delays stack in the remux that no flag
          removed — the MP4 muxer shifts video by the B-frame reorder delay (measured at
          83 ms on these files, while the source Matroska has both streams at 0.000) and the
          AAC encoder adds its own priming. Whether a given browser compensates is not
          something the server can know, so the viewer trims it by ear once.
        */}
        {onAudioOffset && (
          <span className="pc__sync">
            <button type="button" className="pc__btn" onClick={() => onAudioOffset(audioOffsetMs - 25)} aria-label="Sesi 25 ms öne al">
              −
            </button>
            <span className="pc__syncval" title="Ses gecikmesi">
              {audioOffsetMs > 0 ? '+' : ''}
              {audioOffsetMs}ms
            </span>
            <button type="button" className="pc__btn" onClick={() => onAudioOffset(audioOffsetMs + 25)} aria-label="Sesi 25 ms geciktir">
              +
            </button>
          </span>
        )}

        <button
          type="button"
          className="pc__btn"
          onClick={toggleFullscreen}
          aria-label={fullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
        >
          <Icon name={fullscreen ? 'fullscreenExit' : 'fullscreen'} size={1} />
        </button>
      </div>
    </>
  );
}
